import React from "react";
import * as Router from "next/router";
import * as Sentry from "@sentry/browser";
import styled from "styled-components";
import * as D from "draft-js";

import * as U from "./Utils";
import * as u from "../utils";
import * as T from "./Typography";
import * as I from "./Icons";
import ActivityPostHead from "./ActivityPostHead";
import ActivityLike from "./ActivityLike";
import ActivityComment from "./ActivityComment";
import DraftCss from "./DraftCss";
import ImageModal from "./ImageModal";
import { MentionRead, mention } from "./TextAreaMention";
import { LinkRead, link } from "./TextAreaLink";

const decorator = new D.CompositeDecorator([
  { component: MentionRead, strategy: mention() },
  { component: LinkRead, strategy: link() },
]);

const CHAR_LIMIT = 300;

export default function Activity(props) {
  const router = Router.useRouter();

  const [showAll, setShowAll] = React.useState(
    router.asPath.includes("post") || props.post.length < CHAR_LIMIT
  );
  const [showImageModal, setShowImageModal] = React.useState({});
  const numComm = props.reaction_counts?.comm;
  const urls = props?.assets?.map((elm) => elm.url);
  const editorState = React.useMemo(getEditorState, []);
  const initialEditorState = showAll
    ? editorState
    : truncateEditorState(editorState, CHAR_LIMIT);

  return (
    <S.Container>
      {showImageModal.src && (
        <ImageModal
          onClose={() => setShowImageModal(false)}
          src={showImageModal.src}
        />
      )}
      <S.InnerContainer>
        <ActivityPostHead {...props} />
        <S.Body>
          <DraftCss />
          <D.Editor
            editorKey={props.editorKey}
            editorState={initialEditorState}
            readOnly={true}
          />
          {props.video?.src && getVideoContent()}
        </S.Body>
        {!showAll && <T.A onClick={() => setShowAll(true)}>See more</T.A>}
        {urls && getMediaContent()}
        <S.Summary>
          <ActivityLike {...props} />
          {getCommSummary()}
        </S.Summary>
      </S.InnerContainer>
      <ActivityComment {...props} />
    </S.Container>
  );

  function getVideoContent() {
    return (
      <S.VideoContainer>
        <S.Video src={`${props.video.src}`} allow="fullscreen;" />
      </S.VideoContainer>
    );
  }

  function getMediaContent() {
    if (urls.length == 1)
      return (
        <S.MediaContainer count={urls.length}>
          {urls.map(getMediaImage)}
        </S.MediaContainer>
      );

    return <ImageCarousel />;
  }

  function ImageCarousel() {
    const carouselRef = React.useRef(null);
    const [canScroll, setCanScroll] = React.useState({
      left: false,
      right: true,
    });
    React.useEffect(() => {
      getCanScroll();
    }, [carouselRef]);

    return (
      <S.ImageCarousel onScroll={getCanScroll} ref={carouselRef}>
        <S.LeftArrow
          canScroll={canScroll.left}
          onClick={() => scrollCarousel(-550)}
        >
          <I.Arrow />
        </S.LeftArrow>
        {urls.map(getMediaImage)}
        <S.RightArrow
          canScroll={canScroll.right}
          onClick={() => scrollCarousel(550)}
        >
          <I.Arrow />
        </S.RightArrow>
      </S.ImageCarousel>
    );

    function getCanScroll() {
      if (!carouselRef.current) return;
      const carousel = carouselRef.current;
      const canScrollLeft = carousel.scrollLeft !== 0;
      const canScrollRight =
        carousel.scrollLeft < carousel.scrollWidth - carousel.clientWidth;
      setCanScroll({ left: canScrollLeft, right: canScrollRight });
    }

    function scrollCarousel(distance) {
      carouselRef.current.scrollBy({ left: distance, behavior: "smooth" });
    }
  }

  function getMediaImage(elm, idx) {
    return (
      <S.MediaImage
        key={idx}
        src={elm}
        onClick={() => setShowImageModal({ src: elm })}
        onError={(event) => {
          if (
            event.target.src.includes("_800x800") ||
            event.target.src.includes("_400x400")
          ) {
            event.target.src = event.target.src
              .replace("_800x800", "")
              .replace("_400x400", "");
            return;
          }
          Sentry.captureMessage("missing image in ActivityPost", {
            url: event.target.src,
          });
          event.target.style.display = "none";
        }}
      />
    );
  }

  function getCommSummary() {
    return (
      <S.WriteContainer>
        <I.Write />
        <U.Space width={3} />
        <T.CM>{numComm}</T.CM>
      </S.WriteContainer>
    );
  }

  function getEditorState() {
    if (props.editorContent) {
      const content = D.convertFromRaw(props.editorContent);
      return D.EditorState.createWithContent(content, decorator);
    } else {
      const INIT_STATE = D.ContentState.createFromText(props[props.verb]);
      return D.EditorState.createWithContent(INIT_STATE, decorator);
    }
  }

  function truncateEditorState(editorState, charCount) {
    const contentState = editorState.getCurrentContent();
    const blocks = contentState.getBlockMap();

    let count = 0;
    let isTruncated = false;
    const truncatedBlocks = [];
    blocks.forEach((block) => {
      if (!isTruncated) {
        const length = block.getLength();
        if (count + length > charCount) {
          isTruncated = true;
          const truncatedText = block.getText().slice(0, charCount - count);
          const state = D.ContentState.createFromText(`${truncatedText}...`);
          truncatedBlocks.push(state.getFirstBlock());
        } else {
          truncatedBlocks.push(block);
        }
        count += length + 1;
      }
    });

    if (isTruncated) {
      const state = D.ContentState.createFromBlockArray(truncatedBlocks);
      return D.EditorState.createWithContent(state);
    }

    return editorState;
  }
}

const S = {};

S.Container = styled.div`
  border: 1px solid ${u.colours.shellOverlay};
  background-color: white;
  border-radius: 5px;
  margin-top: 20px;
  width: 100%;
`;

S.InnerContainer = styled.div`
  border-bottom: 1px solid ${u.colours.shellOverlay};
  padding: 20px;
  position: relative;
`;

S.Body = styled.div`
  margin-top: 15px;
  font-size: 1.6rem;
  letter-spacing: 0.5px;
  color: ${u.colours.charcoal};
  white-space: pre-line;
`;

S.MediaContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 10px;
  ${(props) =>
    props.count < 2
      ? `
    /* height: 400px; */
  `
      : props.count < 3
        ? `
    /* height: 500px; */
    > div, img { &:nth-child(1) { margin-bottom: 5px; } }
  `
        : ``}
`;

S.MediaImage = styled.img`
  height: 100%;
  max-width: 80%;
  border-radius: 5px;
  object-fit: contain;
  object-position: 50% 50%;
  background: rgb(249, 244, 241);
  border: 1px solid ${u.colours.shellOverlay};
  flex: 1;
`;

S.VideoContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  padding-top: 25px;
  height: 0;
  margin: 20px 0 0 0;
`;

S.Video = styled.iframe`
  border: none;
  border-radius: 5px;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

S.Summary = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 15px;
`;

S.WriteContainer = styled.div`
  display: flex;
  align-items: center;
  color: rgb(101, 103, 107);
  svg {
    color: rgb(101, 103, 107);
  }
`;

S.Text = styled.p`
  font-size: 1.4rem;
  color: ${u.colours.charcoal};
  margin-left: 4px;
`;

S.ImageCarousel = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  height: 274px;
  overflow-x: auto;
  overflow-y: hidden;
  margin: 25px -16px;
  > div {
    flex-shrink: 0;
  }
  -ms-overflow-style: none; /* IE 11 */
  scrollbar-width: none; /* Firefox 64 */
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    height: 0px;
    width: 0px;
  }
  @media ${u.device.tablet} {
    gap: 30px;
  }
`;

S.LeftArrow = styled.div`
  align-items: center;
  background: white;
  border: 1px solid ${u.colours.shellOverlay};
  border-radius: 25px;
  display: none;
  height: 32px;
  justify-content: center;
  left: 4px;
  margin: 0px;
  opacity: ${(p) => (p.canScroll ? "1" : "0")};
  position: absolute;
  transform: rotate(180deg);
  transition: all 0.2s ease-out;
  width: 32px;
  ${(p) =>
    p.canScroll &&
    `:hover {
      cursor: pointer;
      background: rgb(200, 200, 200);
      border: 1px solid rgb(200, 200, 200);
    }`}
  @media (min-width: 520px) {
    display: flex;
  }
  @media ${u.device.tablet} {
    left: 24px;
  }
`;

S.RightArrow = styled.div`
  align-items: center;
  background: white;
  border: 1px solid ${u.colours.shellOverlay};
  border-radius: 25px;
  display: none;
  height: 32px;
  justify-content: center;
  margin-right: 0px;
  opacity: ${(p) => (p.canScroll ? "1" : "0")};
  position: absolute;
  right: 4px;
  transition: all 0.2s ease-out;
  width: 32px;
  ${(p) =>
    p.canScroll &&
    `:hover {
    cursor:  pointer;
    background: rgb(200, 200, 200);
    border: 1px solid rgb(200, 200, 200);
  }`}
  @media (min-width: 520px) {
    display: flex;
  }
  @media ${u.device.tablet} {
    right: 24px;
  }
`;
